import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import hash from 'object-hash'
import { identity } from 'ramda'

import { range6m1yAll } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { chartPalette, datetimeXAxis } from '../utils/constants'
import type { BaseContext, RequestModifier } from './'
import { CommonChartConfig } from './common'

const ECOMMERCE_CHARTS_PATH = '/api/charts/ecommerce'

export namespace Ecommerce {
  export namespace Enjoei {
    const CONTEXT_ID = 'equity:ecommerce:enjoei'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'enju_farm_a_venda'
        | 'enju_farm_seguidores'
        | 'enju_farm_vendidos'
        | 'enju_farm_seguindo'
        | 'enju_farm_yeyezados'
        | 'cantao_a_venda'
        | 'cantao_seguidores'
        | 'cantao_vendidos'
        | 'cantao_seguindo'
        | 'cantao_yeyezados'
        | 'enjoei_pro_a_venda'
        | 'enjoei_pro_seguidores'
        | 'enjoei_pro_vendidos'
        | 'enjoei_pro_seguindo'
        | 'enjoei_pro_yeyezados'
        | 'clubemarisol_a_venda'
        | 'clubemarisol_seguidores'
        | 'clubemarisol_vendidos'
        | 'clubemarisol_seguindo'
        | 'clubemarisol_yeyezados'
      context: typeof CONTEXT_ID
      params?: Partial<AxiosRequestConfig>
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const getChartData = (
      config: ChartConfig,
      requestModifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (
        conf
      ) => conf
    ) =>
      getCompassAPIChartData(config, (conf) =>
        requestModifier({ ...conf, params: config.params ?? undefined })
      )

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          tooltip: { pointFormatter: magnitudePointFormatter() },
        })(config, data),
    }
  }

  export namespace Meli {
    const CONTEXT_ID = 'equity:ecommerce:meli'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'argentina_gmv_daily_000s'
        | 'argentina_gmv_trailing_7_days_yy_growth'
        | 'argentina_gmv_monthly_2_growth'
        | 'argentina_gmv_monthly_yy_growth'
        | 'argentina_gmv_quarterly_yy_growth'
        | 'argentina_gmv_2y_cagr'
        | 'argentina_units_daily_000s'
        | 'argentina_units_trailing_7_days_yy_growth'
        | 'argentina_units_monthly_2_growth'
        | 'argentina_units_monthly_yy_growth'
        | 'argentina_units_quarterly_yy_growth'
        | 'argentina_units_2y_cagr'
        | 'argentina_logistic_type_units'
        | 'argentina_logistic_type_gmv'
        | 'argentina_free_shipping_units'
        | 'argentina_free_shipping_gmv'
        | 'argentina_share_1p_units'
        | 'argentina_share_1p_gmv'
        | 'argentina_official_stores_units'
        | 'argentina_official_stores_gmv'
        | 'argentina_price_buckets_share_units'
        | 'argentina_price_buckets_share_gmv'
        | 'brazil_gmv_daily_000s'
        | 'brazil_gmv_trailing_7_days_yy_growth'
        | 'brazil_gmv_monthly_2_growth'
        | 'brazil_gmv_monthly_yy_growth'
        | 'brazil_gmv_quarterly_yy_growth'
        | 'brazil_gmv_2y_cagr'
        | 'brazil_units_daily_000s'
        | 'brazil_units_trailing_7_days_yy_growth'
        | 'brazil_units_monthly_2_growth'
        | 'brazil_units_monthly_yy_growth'
        | 'brazil_units_quarterly_yy_growth'
        | 'brazil_units_2y_cagr'
        | 'brazil_logistic_type_units'
        | 'brazil_logistic_type_gmv'
        | 'brazil_free_shipping_units'
        | 'brazil_free_shipping_gmv'
        | 'brazil_share_1p_units'
        | 'brazil_share_1p_gmv'
        | 'brazil_official_stores_units'
        | 'brazil_official_stores_gmv'
        | 'brazil_price_buckets_share_units'
        | 'brazil_price_buckets_share_gmv'
        | 'mexico_gmv_daily_000s'
        | 'mexico_gmv_trailing_7_days_yy_growth'
        | 'mexico_gmv_monthly_2_growth'
        | 'mexico_gmv_monthly_yy_growth'
        | 'mexico_gmv_quarterly_yy_growth'
        | 'mexico_gmv_2y_cagr'
        | 'mexico_units_daily_000s'
        | 'mexico_units_trailing_7_days_yy_growth'
        | 'mexico_units_monthly_2_growth'
        | 'mexico_units_monthly_yy_growth'
        | 'mexico_units_quarterly_yy_growth'
        | 'mexico_units_2y_cagr'
        | 'mexico_logistic_type_units'
        | 'mexico_logistic_type_gmv'
        | 'mexico_free_shipping_units'
        | 'mexico_free_shipping_gmv'
        | 'mexico_share_1p_units'
        | 'mexico_share_1p_gmv'
        | 'mexico_official_stores_units'
        | 'mexico_official_stores_gmv'
        | 'overall_gmv_daily_000s'
        | 'overall_gmv_trailing_7_days_yy_growth'
        | 'overall_gmv_monthly_2_growth'
        | 'overall_gmv_monthly_yy_growth'
        | 'overall_gmv_quarterly_yy_growth'
        | 'overall_gmv_2y_cagr'
        | 'overall_units_daily_000s'
        | 'overall_units_trailing_7_days_yy_growth'
        | 'overall_units_monthly_2_growth'
        | 'overall_units_monthly_yy_growth'
        | 'overall_units_quarterly_yy_growth'
        | 'overall_units_2y_cagr'
        | 'overall_logistic_type_units'
        | 'overall_logistic_type_gmv'
        | 'overall_free_shipping_units'
        | 'overall_free_shipping_gmv'
        | 'overall_share_1p_units'
        | 'overall_share_1p_gmv'
        | 'overall_official_stores_units'
        | 'overall_official_stores_gmv'
      context: typeof CONTEXT_ID
      params?: Partial<AxiosRequestConfig>
    }

    export const getChartData = (
      config: ChartConfig,
      requestModifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (
        conf
      ) => conf
    ) =>
      getCompassAPIChartData(config, (conf) =>
        requestModifier({ ...conf, params: config.params ?? undefined })
      )

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'argentina_gmv_daily_000s':
          case 'argentina_units_daily_000s':
          case 'brazil_gmv_daily_000s':
          case 'brazil_units_daily_000s':
          case 'mexico_gmv_daily_000s':
          case 'mexico_units_daily_000s':
          case 'overall_gmv_daily_000s':
          case 'overall_units_daily_000s':
            return getMultipleChartProps(config, data)
          case 'argentina_gmv_trailing_7_days_yy_growth':
          case 'argentina_gmv_monthly_2_growth':
          case 'argentina_gmv_monthly_yy_growth':
          case 'argentina_gmv_quarterly_yy_growth':
          case 'argentina_gmv_2y_cagr':
          case 'argentina_units_trailing_7_days_yy_growth':
          case 'argentina_units_monthly_2_growth':
          case 'argentina_units_monthly_yy_growth':
          case 'argentina_units_quarterly_yy_growth':
          case 'argentina_units_2y_cagr':
          case 'argentina_free_shipping_units':
          case 'argentina_free_shipping_gmv':
          case 'argentina_share_1p_units':
          case 'argentina_share_1p_gmv':
          case 'argentina_official_stores_units':
          case 'argentina_official_stores_gmv':
          case 'brazil_gmv_trailing_7_days_yy_growth':
          case 'brazil_gmv_monthly_2_growth':
          case 'brazil_gmv_monthly_yy_growth':
          case 'brazil_gmv_quarterly_yy_growth':
          case 'brazil_gmv_2y_cagr':
          case 'brazil_units_trailing_7_days_yy_growth':
          case 'brazil_units_monthly_2_growth':
          case 'brazil_units_monthly_yy_growth':
          case 'brazil_units_quarterly_yy_growth':
          case 'brazil_units_2y_cagr':
          case 'brazil_free_shipping_units':
          case 'brazil_free_shipping_gmv':
          case 'brazil_share_1p_units':
          case 'brazil_share_1p_gmv':
          case 'brazil_official_stores_units':
          case 'brazil_official_stores_gmv':
          case 'mexico_gmv_trailing_7_days_yy_growth':
          case 'mexico_gmv_monthly_2_growth':
          case 'mexico_gmv_monthly_yy_growth':
          case 'mexico_gmv_quarterly_yy_growth':
          case 'mexico_gmv_2y_cagr':
          case 'mexico_units_trailing_7_days_yy_growth':
          case 'mexico_units_monthly_2_growth':
          case 'mexico_units_monthly_yy_growth':
          case 'mexico_units_quarterly_yy_growth':
          case 'mexico_units_2y_cagr':
          case 'mexico_free_shipping_units':
          case 'mexico_free_shipping_gmv':
          case 'mexico_share_1p_units':
          case 'mexico_share_1p_gmv':
          case 'mexico_official_stores_units':
          case 'mexico_official_stores_gmv':
          case 'overall_gmv_trailing_7_days_yy_growth':
          case 'overall_gmv_monthly_2_growth':
          case 'overall_gmv_monthly_yy_growth':
          case 'overall_gmv_quarterly_yy_growth':
          case 'overall_gmv_2y_cagr':
          case 'overall_units_trailing_7_days_yy_growth':
          case 'overall_units_monthly_2_growth':
          case 'overall_units_monthly_yy_growth':
          case 'overall_units_quarterly_yy_growth':
          case 'overall_units_2y_cagr':
          case 'overall_free_shipping_units':
          case 'overall_free_shipping_gmv':
          case 'overall_share_1p_units':
          case 'overall_share_1p_gmv':
          case 'overall_official_stores_units':
          case 'overall_official_stores_gmv':
            return getMultiplePercentChartProps(config, data)
          case 'argentina_logistic_type_units':
          case 'argentina_logistic_type_gmv':
          case 'brazil_logistic_type_units':
          case 'brazil_logistic_type_gmv':
          case 'mexico_logistic_type_units':
          case 'mexico_logistic_type_gmv':
          case 'overall_logistic_type_units':
          case 'overall_logistic_type_gmv':
          case 'argentina_price_buckets_share_units':
          case 'argentina_price_buckets_share_gmv':
          case 'brazil_price_buckets_share_units':
          case 'brazil_price_buckets_share_gmv':
            return getMultiplePercentStackedChartProps(config, data)
        }
      },
    }

    const getMultipleChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          tooltip: { pointFormatter: magnitudePointFormatter() },
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    const getMultiplePercentChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          convertToPercentages: Array(data.data.length).fill(true),
          tooltip: {
            xDateFormat: config.chart.includes('trailing')
              ? '%A, %b %e, %Y'
              : '%B %Y',
          },
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    const getMultiplePercentStackedChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(10).fill(true),
          plotOptions: { area: { stacking: 'normal' } },
          tooltip: { xDateFormat: '%B %Y' },
        },
        {
          type: Array(data.data.length).fill('area'),
        }
      )(config, data)
  }

  export namespace SellersMeli {
    type PickFeedback<T> = T extends `feedback_${string}` ? T : never

    type FeedbackChartId = PickFeedback<ChartConfig['chart']>

    const CONTEXT_ID = 'equity:ecommerce:sellers_meli'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'feedback_percentual_madeira_madeira'
        | 'feedback_madeira_madeira'
        | 'feedback_percentual_mobly'
        | 'feedback_mobly'
        | 'furniture_feedback_ruim'
        | 'furniture_feedback_total'
        | 'furniture_sales_last_60_days'
        | 'feedback_percentual_centauro'
        | 'feedback_centauro'
        | 'feedback_percentual_nike'
        | 'feedback_nike'
        | 'feedback_percentual_adidas'
        | 'feedback_adidas'
        | 'sporty_feedback_ruim'
        | 'sporty_feedback_total'
        | 'sporty_sales_last_60_days'
        | 'feedback_percentual_pao_de_acucar'
        | 'feedback_pao_de_acucar'
        | 'feedback_percentual_extra'
        | 'feedback_extra'
        | 'feedback_percentual_supermercados_mambo'
        | 'feedback_supermercados_mambo'
        | 'food_feedback_ruim'
        | 'food_feedback_total'
        | 'food_sales_last_60_days'
        | 'feedback_percentual_mobcom'
        | 'feedback_mobcom'
        | 'feedback_percentual_webfones'
        | 'feedback_webfones'
        | 'consumer_electronics_feedback_ruim'
        | 'consumer_electronics_feedback_total'
        | 'consumer_electronics_sales_last_60_days'
        | 'feedback_percentual_boticario'
        | 'feedback_boticario'
        | 'beauty_feedback_ruim'
        | 'beauty_feedback_total'
        | 'beauty_sales_last_60_days'
        | 'feedback_percentual_ashua'
        | 'feedback_ashua'
        | 'feedback_percentual_hering'
        | 'feedback_hering'
        | 'feedback_percentual_marisa'
        | 'feedback_marisa'
        | 'feedback_percentual_mini_reserva'
        | 'feedback_mini_reserva'
        | 'feedback_percentual_oficina_reserva'
        | 'feedback_oficina_reserva'
        | 'feedback_percentual_reserva'
        | 'feedback_reserva'
        | 'feedback_percentual_reserva_ink'
        | 'feedback_reserva_ink'
        | 'feedback_percentual_my_shoes'
        | 'feedback_my_shoes'
        | 'fashion_feedback_ruim'
        | 'fashion_feedback_total'
        | 'fashion_sales_last_60_days'
        | 'feedback_percentual_olist'
        | 'feedback_olist'
        | 'others_feedback_ruim'
        | 'others_feedback_total'
        | 'others_sales_last_60_days'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'furniture_feedback_ruim':
          case 'furniture_feedback_total':
          case 'furniture_sales_last_60_days':
          case 'sporty_feedback_ruim':
          case 'sporty_feedback_total':
          case 'sporty_sales_last_60_days':
          case 'food_feedback_ruim':
          case 'food_feedback_total':
          case 'food_sales_last_60_days':
          case 'consumer_electronics_feedback_ruim':
          case 'consumer_electronics_feedback_total':
          case 'consumer_electronics_sales_last_60_days':
          case 'beauty_feedback_ruim':
          case 'beauty_feedback_total':
          case 'beauty_sales_last_60_days':
          case 'fashion_feedback_ruim':
          case 'fashion_feedback_total':
          case 'fashion_sales_last_60_days':
          case 'others_feedback_ruim':
          case 'others_feedback_total':
          case 'others_sales_last_60_days':
            return getLineChartProps(config, data)
          default:
            return getStackedChartProps(config, data)
        }
      },
    }

    const isPercentual = (chart: ChartConfig['chart']) =>
      chart.includes('percentual')

    const isFeedbackRuim = (chart: ChartConfig['chart']) =>
      chart.includes('feedback_ruim')

    const getComparisonTitle = (chart: ChartConfig['chart']) =>
      chart.includes('feedback_ruim')
        ? 'Bad feedback'
        : chart.includes('feedback_total')
        ? 'Total feedback'
        : chart.includes('sales_last_60_days')
        ? 'Sales: last 60 days'
        : undefined

    const FEEDBACK_CHART_TITLES: Record<FeedbackChartId, string> = {
      feedback_percentual_madeira_madeira: 'Madeira Madeira (Percentual)',
      feedback_percentual_mobly: 'Mobly (Percentual)',
      feedback_madeira_madeira: 'Madeira Madeira',
      feedback_mobly: 'Mobly',
      feedback_percentual_centauro: 'Centauro (Percentual)',
      feedback_percentual_nike: 'Nike (Percentual)',
      feedback_percentual_adidas: 'Adidas (Percentual)',
      feedback_centauro: 'Centauro',
      feedback_nike: 'Nike',
      feedback_adidas: 'Adidas',
      feedback_percentual_pao_de_acucar: 'Pão de açucar (Percentual)',
      feedback_percentual_extra: 'Extra (Percentual)',
      feedback_percentual_supermercados_mambo:
        'Supermercados Mambo (Percentual)',
      feedback_pao_de_acucar: 'Pão de açucar',
      feedback_extra: 'Extra',
      feedback_supermercados_mambo: 'Supermercados Mambo',
      feedback_percentual_mobcom: 'Mobcom (Percentual)',
      feedback_percentual_webfones: 'Webfones (Percentual)',
      feedback_mobcom: 'Mobcom',
      feedback_webfones: 'Webfones',
      feedback_percentual_boticario: 'O Boticário (Percentual)',
      feedback_boticario: 'O Botícário',
      feedback_percentual_hering: 'Hering (Percentual)',
      feedback_percentual_ashua: 'ASHUA (Percentual)',
      feedback_percentual_marisa: 'Marisa (Percentual)',
      feedback_percentual_my_shoes: 'My Shoes (Percentual)',
      feedback_my_shoes: 'My Shoes',
      feedback_hering: 'Hering',
      feedback_ashua: 'ASHUA',
      feedback_marisa: 'Marisa',
      feedback_percentual_mini_reserva: 'Mini Reserva (Percentual)',
      feedback_percentual_oficina_reserva: 'Oficina Reserva (Percentual)',
      feedback_mini_reserva: 'Mini Reserva',
      feedback_oficina_reserva: 'Oficina Reserva',
      feedback_percentual_reserva: 'Reserva (Percentual)',
      feedback_percentual_reserva_ink: 'Reserva Ink (Percentual)',
      feedback_reserva: 'Reserva',
      feedback_reserva_ink: 'Reserva Ink',
      feedback_percentual_olist: 'Olist (Percentual)',
      feedback_olist: 'Olist',
    }

    const getStackedChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          chartTitle: FEEDBACK_CHART_TITLES[config.chart as FeedbackChartId],
          ...(isPercentual(config.chart)
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          plotOptions: { area: { stacking: 'normal' } },
        },
        {
          type: Array(data.data.length).fill('area'),
        }
      )(config, data)

    const getLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        chartTitle: getComparisonTitle(config.chart),
        ...(isFeedbackRuim(config.chart)
          ? { convertToPercentages: Array(2).fill(true) }
          : {}),
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: isFeedbackRuim(config.chart) ? '%' : '',
          }),
        },
      })(config, data)
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:ecommerce:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'ev_sales_mglu3'
        | 'ev_sales_amer3'
        | 'ev_sales_viia3'
        | 'ev_sales_enju3'
        | 'ev_sales_west3'
        | 'ev_sales_petz3'
        | 'ev_sales_meli-us'
        | 'ev_sales_mbly3'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getValuationChartProps(config, data),
    }

    const getValuationChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)
  }

  export namespace CategorySubsection {
    const CONTEXT_ID = 'equity:ecommerce:category_subsection'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type MeliCategoryChartId =
      | 'meli_category_asp'
      | 'meli_category_units'
      | 'meli_category_gmv'

    type MeliCountries = 'argentina' | 'brazil' | 'mexico' | 'overall'

    export interface ChartConfig {
      title?: string
      chart: MeliCategoryChartId
      region: MeliCountries
      series: string[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const MELI_CATEGORY_CHART_PATHS: Record<MeliCategoryChartId, string> = {
      meli_category_asp: '/meli/category-asp',
      meli_category_units: '/meli/category-units',
      meli_category_gmv: '/meli/category-gmv',
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: RequestModifier = identity
    ) => {
      const response = (await axios(
        modifier({
          url: ECOMMERCE_CHARTS_PATH + MELI_CATEGORY_CHART_PATHS[config.chart],
          method: 'POST',
          data: {
            region: config.region,
            series: config.series,
          },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace EnjoeiVendidosMMDelta {
    const CONTEXT_ID = 'equity:ecommerce:enjoei_vendidos_mm_delta'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      store: string
      mm: number
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (
      config: ChartConfig,
      modifier: RequestModifier = identity
    ) => {
      const response = (await axios(
        modifier({
          url: ECOMMERCE_CHARTS_PATH + '/enjoei/vendidos-mm-delta',
          method: 'POST',
          data: { store: config.store, mm: config.mm },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        tooltip: { xDateFormat: '%A, %b %e, %Y' },
        xAxis: datetimeXAxis[0],
      })(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace PriceBuckets {
    const CONTEXT_ID = 'equity:ecommerce:price_buckets'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title: string
      region: 'argentina' | 'brazil'
      series: string[]
      context: typeof CONTEXT_ID
    }

    export interface ChartData extends CompassAPIDataBase {
      data: {
        series_id: string
        name: string
        series_type: string | null
        values: {
          date: string
          value: number
        }[]
      }[]
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: RequestModifier = identity
    ) => {
      const response = (await axios(
        modifier({
          url: ECOMMERCE_CHARTS_PATH + '/meli/price-buckets',
          method: 'GET',
          params: {
            region: config.region,
            series: config.series,
          },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((series) => {
        const type = series.series_id.split('_').pop()
        return {
          id: series.series_id,
          name: series.name.split('_').join(' '),
          type: 'spline',
          yAxis: type,
          data: series.values.map(({ date, value }) => [
            new Date(date).getTime(),
            value,
          ]),
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            valueDecimals: 2,
            xDateFormat: '%A, %b %e, %Y',
          },
        }
      })

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'units',
          opposite: false,
          title: { text: 'Units' },
        },
        {
          id: 'gmv',
          opposite: true,
          title: { text: 'GMV' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            colors: chartPalette,
            series: series,
            yAxis,
            rangeSelector: range6m1yAll,
          },
        ]),
        stockTools: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Westwing {
    const CONTEXT_ID = 'equity:ecommerce:westwing'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type WestwingChartId =
      | 'all_products_price_bucket'
      | 'all_products_categories'
      | 'all_products_installments'
      | 'all_products_westwing_collection'
      | 'relevant_products_price_bucket'
      | 'relevant_products_categories'
      | 'relevant_products_installments'
      | 'relevant_products_westwing_collection'
      | 'price_bucket_westwing_collection'
      | 'categories_westwing_collection'
      | 'installments_westwing_collection'

    export interface ChartConfig {
      title?: string
      isPercentage: boolean
      chart: WestwingChartId
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const WESTWING_CHART_PATHS: Record<WestwingChartId, string> = {
      all_products_price_bucket: '/westwing/all-products-price-bucket',
      all_products_categories: '/westwing/all-products-categories',
      all_products_installments: '/westwing/all-products-installments',
      all_products_westwing_collection:
        '/westwing/all-products-westwing-collection',
      relevant_products_price_bucket:
        '/westwing/relevant-products-price-bucket',
      relevant_products_categories: '/westwing/relevant-products-categories',
      relevant_products_installments:
        '/westwing/relevant-products-installments',
      relevant_products_westwing_collection:
        '/westwing/relevant-products-westwing-collection',
      price_bucket_westwing_collection:
        '/westwing/price-bucket-westwing-collection',
      categories_westwing_collection:
        '/westwing/categories-westwing-collection',
      installments_westwing_collection:
        '/westwing/installments-westwing-collection',
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: RequestModifier = identity
    ) => {
      const response = (await axios(
        modifier({
          url: ECOMMERCE_CHARTS_PATH + WESTWING_CHART_PATHS[config.chart],
          method: 'POST',
          data: { isPercentage: config.isPercentage },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: config.isPercentage,
        }),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: s.name,
        type: config.isPercentage
          ? data.data.length === 1
            ? 'spline'
            : 'area'
          : 'spline',
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: config.isPercentage ? '%' : '',
          }),
          valueDecimals: 2,
          valueSuffix: config.isPercentage ? '%' : '',
          xDateFormat: '%A, %b %e, %Y',
        },
        ...(data.data.length === 1 ? { showInLegend: false } : {}),
        yAxis: 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const PERCENTAGE_CHARTS = [
        'all_products_price_bucket',
        'all_products_categories',
        'relevant_products_price_bucket',
        'relevant_products_categories',
        'price_bucket_westwing_collection',
        'categories_westwing_collection',
      ]

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: `{value}${config.isPercentage ? '%' : ''}` },
          min:
            PERCENTAGE_CHARTS.includes(config.chart) && config.isPercentage
              ? 0
              : undefined,
          max:
            PERCENTAGE_CHARTS.includes(config.chart) && config.isPercentage
              ? 100
              : undefined,
        },
      ]

      const plotOptions = {
        area: { stacking: config.isPercentage ? 'normal' : undefined },
      }

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions,
            colors: chartPalette,
            tooltip: {
              shared: PERCENTAGE_CHARTS.includes(config.chart),
              split: !PERCENTAGE_CHARTS.includes(config.chart),
            },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
